import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { OrganizationAvatar } from "@/components/organization-avatar";
import { OrganizationSelector } from "@/components/organization-selector";
import { apiRequest } from "@/lib/queryClient";
import type { Gewerk, Typ, Status, Plan, Position } from "@mangelapp/shared";
import { AppShell } from "@/components/layouts/AppShell";
import { PageHeaderLayout } from "@/components/layouts/PageHeaderLayout";
import { HeaderActionGroup, HeaderSearchInput, HeaderTabNav } from "@/components/layouts/header-slots";
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next';
import { useLocation } from "wouter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ColorPicker } from "@/components/ui/color-picker";
import { Checkbox } from "@/components/ui/checkbox";
import { CornerLabelField } from "@/components/ui/corner-label-field";

import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { PermissionButton } from "@/components/PermissionButton";
import { PermissionInput } from "@/components/PermissionInput";
import { useCurrentOrganization } from "@/hooks/use-current-organization";
import { useOrganizationSelector } from "@/hooks/use-organization-selector";
import { AccessDenied } from "@/components/AccessDenied";
import { OrganizationDocumentsTab } from "@/components/OrganizationDocumentsTab";
import { RequirePermission } from "@/components/RequirePermission";
import { useAlert } from "@/hooks/use-alert";
import { LogoLoader } from "@/components/ui/logo-loader";
import { RightSidebar, SidebarButtonGroup, SidebarIconButton } from '@/components/ui/right-sidebar';
import { IconButtonCancel, IconButtonSave } from '@/components/ui/icon-buttons';
import iconDeleteSvg from '@/assets/circle_delete.svg';
import iconEditSvg from '@/assets/circle_edit.svg';

export default function Listen() {
  const { toast } = useToast();
  const { t, i18n } = useTranslation(['pages.listen', 'common']);
  const { confirm } = useAlert();
  const rqClient = useQueryClient();
  
  // Check permissions using the new organization-based system
  const { hasPermission } = usePermissions();

  const { organizationId } = useCurrentOrganization();
  
  // Check if user is contractor in the selected organization
  const { isContractor } = useOrganizationSelector();
  
  // All hooks must be called before any early returns
  const [sortBy, setSortBy] = useState("name"); // "name" or "recent"
  const [sortColumn, setSortColumn] = useState<string | null>(null); // for column-specific sorting
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [activeTab, setActiveTab] = useState("position");
  const [location, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [isNewEntryModalOpen, setIsNewEntryModalOpen] = useState(false);
  const [newEntryText, setNewEntryText] = useState("");
  const [newEntryColor, setNewEntryColor] = useState("#3B82F6"); // Default color for new statuses
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [editText, setEditText] = useState("");
  const [editColor, setEditColor] = useState("#3B82F6");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const deleteSelectedDocumentsRef = useRef<(() => void) | null>(null);
  const editSelectedDocumentRef = useRef<(() => Promise<void>) | null>(null);
  const handleDeleteSelectedReady = useCallback((fn: () => void) => {
    deleteSelectedDocumentsRef.current = fn;
  }, []);
  const handleEditSelectedReady = useCallback((fn: () => Promise<void>) => {
    editSelectedDocumentRef.current = fn;
  }, []);
  const [documentSelectionCount, setDocumentSelectionCount] = useState(0);
  // State to trigger document upload from header button
  const [triggerDocumentUpload, setTriggerDocumentUpload] = useState(false);

  const allowedTabs = useMemo(() => {
    const tabs = ["position", "gewerk"];
    if (hasPermission("manage_lists")) {
      tabs.push("typ", "status", "priority");
    }
    if (hasPermission("manage_lists")) {
      tabs.push("documents");
    }
    return tabs;
  }, [hasPermission]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");

    if (tabParam && allowedTabs.includes(tabParam)) {
      setActiveTab(tabParam);
      return;
    }

    setActiveTab("position");
  }, [location, allowedTabs]);

  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      const params = new URLSearchParams(window.location.search);
      params.set("tab", tab);
      setLocation(`/listen?${params.toString()}`, { replace: true });
    },
    [setLocation]
  );
  
  // Fetch data from backend
  const { data: gewerkeResponse, isLoading: isLoadingGewerke } = useQuery({
    queryKey: ["/api/gewerke", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/gewerke?organizationId=${encodeURIComponent(organizationId)}` : "/api/gewerke";
      const response = await apiRequest(url);
      return await response.json();
    },
  });

  const gewerke = gewerkeResponse?.data || [];

  const { data: typenResponse, isLoading: isLoadingTypen } = useQuery({
    queryKey: ["/api/typen", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/typen?organizationId=${encodeURIComponent(organizationId)}` : "/api/typen";
      const response = await apiRequest(url);
      return await response.json();
    },
  });

  const typen = typenResponse?.data || [];

  const { data: statusResponse, isLoading: isLoadingStatus } = useQuery({
    queryKey: ["/api/status", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/status?organizationId=${encodeURIComponent(organizationId)}` : "/api/status";
      const response = await apiRequest(url);
      return await response.json();
    },
  });

  const status = statusResponse?.data || [];

  const { data: prioritiesResponse, isLoading: isLoadingPriorities } = useQuery({
    queryKey: ["/api/priorities", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/priorities?organizationId=${encodeURIComponent(organizationId)}` : "/api/priorities";
      const response = await apiRequest(url);
      const result = await response.json();
      return result;
    },
    enabled: !!organizationId,
    refetchOnMount: 'always',
    staleTime: 0, // Always consider data stale to ensure fresh fetch
  });

  const priorities = prioritiesResponse?.data || [];

  // Invalidate priorities query when organizationId changes to ensure fresh data
  useEffect(() => {
    if (organizationId) {
      rqClient.invalidateQueries({ queryKey: ["/api/priorities", organizationId] });
    }
  }, [organizationId, rqClient]);

  const { data: positionsResponse, isLoading: isLoadingPositions } = useQuery({
    queryKey: ["/api/positions", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/positions?organizationId=${encodeURIComponent(organizationId)}` : "/api/positions";
      const response = await apiRequest(url);
      return await response.json();
    },
  });

  const positions = positionsResponse?.data || [];

  // Fetch organization documents
  const { data: documentsResponse, isLoading: isLoadingDocuments } = useQuery({
    queryKey: ["/api/documents/organization", organizationId],
    queryFn: async () => {
      const url = organizationId
        ? `/api/documents/organization/${encodeURIComponent(organizationId)}?limit=200&offset=0`
        : null;
      if (!url) return { data: [] };
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId && activeTab === 'documents',
  });

  const orgDocuments = documentsResponse?.data || [];

  // Mutations for creating entries
  const createGewerkMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      console.log('🔧 Creating gewerk:', data);
      
      const response = await apiRequest('/api/gewerke', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      console.log('✅ Gewerk created successfully:', result);
      return result;
    },
    onSuccess: async (newGewerk) => {
      console.log('✅ Gewerk creation success callback:', newGewerk);
      
      // Optimistic update: immediately update the cache
      rqClient.setQueryData(["/api/gewerke"], (oldData: any) => {
        if (!oldData) return oldData;
        const prev = Array.isArray(oldData) ? oldData : (Array.isArray(oldData?.data) ? oldData.data : []);
        const newItem = newGewerk?.data || newGewerk;
        return [...prev, newItem].filter(Boolean);
      });
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/gewerke"] });
      
      setIsNewEntryModalOpen(false);
      setNewEntryText("");
      toast({ title: t('messages.success.created') });
    },
    onError: (error) => {
      console.error('❌ Gewerk creation mutation error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/gewerke"] });
      toast({ 
        title: t('messages.error.create'), 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const createTypMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      console.log('🔧 Creating typ:', data);
      
      const response = await apiRequest('/api/typen', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      console.log('✅ Typ created successfully:', result);
      return result;
    },
    onSuccess: async (newTyp) => {
      console.log('✅ Typ creation success callback:', newTyp);
      
      // Optimistic update: immediately update the cache
      rqClient.setQueryData(["/api/typen"], (oldData: any) => {
        if (!oldData) return oldData;
        const prev = Array.isArray(oldData) ? oldData : (Array.isArray(oldData?.data) ? oldData.data : []);
        const newItem = newTyp?.data || newTyp;
        return [...prev, newItem].filter(Boolean);
      });
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/typen"] });
      
      setIsNewEntryModalOpen(false);
      setNewEntryText("");
      toast({ title: t('messages.success.created') });
    },
    onError: (error) => {
      console.error('❌ Typ creation mutation error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/typen"] });
      toast({ 
        title: t('messages.error.create'), 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const createStatusMutation = useMutation({
    mutationFn: async (data: { name: string; color?: string }) => {
      console.log('🔧 Creating status:', data);
      
      const response = await apiRequest('/api/status', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      console.log('✅ Status created successfully:', result);
      return result;
    },
    onSuccess: async (newStatus) => {
      console.log('✅ Status creation success callback:', newStatus);
      
      // Optimistic update: immediately update the cache
      rqClient.setQueryData(["/api/status"], (oldData: any) => {
        if (!oldData) return oldData;
        const prev = Array.isArray(oldData) ? oldData : (Array.isArray(oldData?.data) ? oldData.data : []);
        const newItem = newStatus?.data || newStatus;
        return [...prev, newItem].filter(Boolean);
      });
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/status"] });
      
      setIsNewEntryModalOpen(false);
      setNewEntryText("");
      setNewEntryColor("#3B82F6"); // Reset to default
      toast({ title: t('messages.success.created') });
    },
    onError: (error) => {
      console.error('❌ Status creation mutation error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/status"] });
      toast({ 
        title: t('messages.error.create'), 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Mutation for updating status
  const updateStatusMutation = useMutation({
    mutationFn: async (data: { id: string; name: string; color?: string }) => {
      console.log('🔧 Updating status:', data);
      
      const response = await apiRequest(`/api/status/${data.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: data.name, color: data.color })
      });
      
      const result = await response.json();
      console.log('✅ Status updated successfully:', result);
      return result;
    },
    onSuccess: async () => {
      console.log('✅ Status update success callback');
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/status"] });
      
      setIsEditModalOpen(false);
      setEditingItem(null);
      setEditText("");
      setEditColor("#3B82F6");
      toast({ title: t('messages.success.updated') || 'Status updated successfully' });
    },
    onError: (error) => {
      console.error('❌ Status update error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/status"] });
      toast({ 
        title: t('messages.error.update') || 'Error updating status', 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Priorities mutations
  const createPriorityMutation = useMutation({
    mutationFn: async (data: { name: string; color?: string }) => {
      console.log('🔧 Creating priority:', data);
      
      const response = await apiRequest('/api/priorities', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      console.log('✅ Priority created successfully:', result);
      return result;
    },
    onSuccess: async (newPriority) => {
      console.log('✅ Priority creation success callback:', newPriority);
      
      // Optimistic update: immediately update the cache
      rqClient.setQueryData(["/api/priorities"], (oldData: any) => {
        if (!oldData) return oldData;
        const prev = Array.isArray(oldData) ? oldData : (Array.isArray(oldData?.data) ? oldData.data : []);
        const newItem = newPriority?.data || newPriority;
        return [...prev, newItem].filter(Boolean);
      });
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/priorities"] });
      
      setIsNewEntryModalOpen(false);
      setNewEntryText("");
      setNewEntryColor("#3B82F6"); // Reset to default
      toast({ title: t('messages.success.created') });
    },
    onError: (error) => {
      console.error('❌ Priority creation mutation error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/priorities"] });
      toast({ 
        title: t('messages.error.create'), 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Mutation for updating priority
  const updatePriorityMutation = useMutation({
    mutationFn: async (data: { id: string; name: string; color?: string }) => {
      console.log('🔧 Updating priority:', data);
      
      const response = await apiRequest(`/api/priorities/${data.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: data.name, color: data.color })
      });
      
      const result = await response.json();
      console.log('✅ Priority updated successfully:', result);
      return result;
    },
    onSuccess: async () => {
      console.log('✅ Priority update success callback');
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/priorities"] });
      
      setIsEditModalOpen(false);
      setEditingItem(null);
      setEditText("");
      setEditColor("#3B82F6");
      toast({ title: t('messages.success.updated') || 'Priority updated successfully' });
    },
    onError: (error) => {
      console.error('❌ Priority update error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/priorities"] });
      toast({ 
        title: t('messages.error.update') || 'Error updating priority', 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const deletePriorityMutation = useMutation({
    mutationFn: async (id: string) => {
      console.log('🗑️ Deleting priority:', id);
      
      await apiRequest(`/api/priorities/${id}`, {
        method: 'DELETE'
      });
      
      console.log('✅ Priority deleted successfully');
      return { id };
    },
    onSuccess: async (_, deletedId) => {
      console.log('✅ Priority deletion success callback:', deletedId);
      
      // Optimistic update: immediately remove from cache
      rqClient.setQueryData(["/api/priorities"], (oldData: any) => {
        if (!oldData) return oldData;
        const prev = Array.isArray(oldData) ? oldData : (Array.isArray(oldData?.data) ? oldData.data : []);
        return prev.filter((item: any) => item.id !== deletedId);
      });
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/priorities"] });
      
      toast({ title: t('messages.success.deleted') });
    },
    onError: (error) => {
      console.error('❌ Priority deletion error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/priorities"] });
      toast({ 
        title: t('messages.error.delete'), 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Mutations for deleting entries
  // Mutation for updating gewerk
  const updateGewerkMutation = useMutation({
    mutationFn: async (data: { id: string; name: string }) => {
      console.log('🔧 Updating gewerk:', data);
      
      const response = await apiRequest(`/api/gewerke/${data.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: data.name })
      });
      
      const result = await response.json();
      console.log('✅ Gewerk updated successfully:', result);
      return result;
    },
    onSuccess: async () => {
      console.log('✅ Gewerk update success callback');
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/gewerke"] });
      
      setIsEditModalOpen(false);
      setEditingItem(null);
      setEditText("");
      toast({ title: t('messages.success.updated') || 'Gewerk updated successfully' });
    },
    onError: (error) => {
      console.error('❌ Gewerk update error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/gewerke"] });
      toast({ 
        title: t('messages.error.update') || 'Error updating gewerk', 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const deleteGewerkMutation = useMutation({
    mutationFn: async (id: string) => {
      console.log('🗑️ Deleting gewerk:', id);
      
      await apiRequest(`/api/gewerke/${id}`, {
        method: 'DELETE'
      });
      
      console.log('✅ Gewerk deleted successfully');
      return { id };
    },
    onSuccess: async (_, deletedId) => {
      console.log('✅ Gewerk deletion success callback:', deletedId);
      
      // Optimistic update: immediately remove from cache
      rqClient.setQueryData(["/api/gewerke"], (oldData: any) => {
        if (!oldData) return oldData;
        const prev = Array.isArray(oldData) ? oldData : (Array.isArray(oldData?.data) ? oldData.data : []);
        return prev.filter((item: any) => item.id !== deletedId);
      });
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/gewerke"] });
      
      toast({ title: t('messages.success.deleted') });
    },
    onError: (error) => {
      console.error('❌ Gewerk deletion error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/gewerke"] });
      toast({ 
        title: t('messages.error.delete'), 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Mutation for updating typ
  const updateTypMutation = useMutation({
    mutationFn: async (data: { id: string; name: string }) => {
      console.log('🔧 Updating typ:', data);
      
      const response = await apiRequest(`/api/typen/${data.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: data.name })
      });
      
      const result = await response.json();
      console.log('✅ Typ updated successfully:', result);
      return result;
    },
    onSuccess: async () => {
      console.log('✅ Typ update success callback');
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/typen"] });
      
      setIsEditModalOpen(false);
      setEditingItem(null);
      setEditText("");
      toast({ title: t('messages.success.updated') || 'Typ updated successfully' });
    },
    onError: (error) => {
      console.error('❌ Typ update error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/typen"] });
      toast({ 
        title: t('messages.error.update') || 'Error updating typ', 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const deleteTypMutation = useMutation({
    mutationFn: async (id: string) => {
      console.log('🗑️ Deleting typ:', id);
      
      await apiRequest(`/api/typen/${id}`, {
        method: 'DELETE'
      });
      
      console.log('✅ Typ deleted successfully');
      return { id };
    },
    onSuccess: async (_, deletedId) => {
      console.log('✅ Typ deletion success callback:', deletedId);
      
      // Optimistic update: immediately remove from cache
      rqClient.setQueryData(["/api/typen"], (oldData: any) => {
        if (!oldData) return oldData;
        const prev = Array.isArray(oldData) ? oldData : (Array.isArray(oldData?.data) ? oldData.data : []);
        return prev.filter((item: any) => item.id !== deletedId);
      });
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/typen"] });
      
      toast({ title: t('messages.success.deleted') });
    },
    onError: (error) => {
      console.error('❌ Typ deletion error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/typen"] });
      toast({ 
        title: t('messages.error.delete'), 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const deleteStatusMutation = useMutation({
    mutationFn: async (id: string) => {
      console.log('🗑️ Deleting status:', id);
      
      await apiRequest(`/api/status/${id}`, {
        method: 'DELETE'
      });
      
      console.log('✅ Status deleted successfully');
      return { id };
    },
    onSuccess: async (_, deletedId) => {
      console.log('✅ Status deletion success callback:', deletedId);
      
      // Optimistic update: immediately remove from cache
      rqClient.setQueryData(["/api/status"], (oldData: any) => {
        if (!oldData) return oldData;
        const prev = Array.isArray(oldData) ? oldData : (Array.isArray(oldData?.data) ? oldData.data : []);
        return prev.filter((item: any) => item.id !== deletedId);
      });
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/status"] });
      
      toast({ title: t('messages.success.deleted') });
    },
    onError: (error) => {
      console.error('❌ Status deletion error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/status"] });
      toast({ 
        title: t('messages.error.delete'), 
        description: error.message,
        variant: "destructive"
      });
    }
  });


  // Position mutations
  const createPositionMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      console.log('🔧 Creating position:', data);
      
      const response = await apiRequest('/api/positions', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      console.log('✅ Position created successfully:', result);
      return result;
    },
    onSuccess: async (newPosition) => {
      console.log('✅ Position creation success callback:', newPosition);
      
      // Optimistic update: immediately update the cache
      rqClient.setQueryData(["/api/positions"], (oldData: any) => {
        if (!oldData) return oldData;
        const prev = Array.isArray(oldData) ? oldData : (Array.isArray(oldData?.data) ? oldData.data : []);
        const newItem = newPosition?.data || newPosition;
        return [...prev, newItem].filter(Boolean);
      });
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/positions"] });
      
      setIsNewEntryModalOpen(false);
      setNewEntryText("");
      toast({ title: t('messages.success.created') });
    },
    onError: (error) => {
      console.error('❌ Position creation mutation error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/positions"] });
      toast({ 
        title: t('messages.error.create'), 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Mutation for updating position
  const updatePositionMutation = useMutation({
    mutationFn: async (data: { id: string; name: string }) => {
      console.log('🔧 Updating position:', data);
      
      const response = await apiRequest(`/api/positions/${data.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: data.name })
      });
      
      const result = await response.json();
      console.log('✅ Position updated successfully:', result);
      return result;
    },
    onSuccess: async () => {
      console.log('✅ Position update success callback');
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/positions"] });
      
      setIsEditModalOpen(false);
      setEditingItem(null);
      setEditText("");
      toast({ title: t('messages.success.updated') || 'Position updated successfully' });
    },
    onError: (error) => {
      console.error('❌ Position update error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/positions"] });
      toast({ 
        title: t('messages.error.update') || 'Error updating position', 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const deletePositionMutation = useMutation({
    mutationFn: async (id: string) => {
      console.log('🗑️ Deleting position:', id);
      
      await apiRequest(`/api/positions/${id}`, {
        method: 'DELETE'
      });
      
      console.log('✅ Position deleted successfully');
      return { id };
    },
    onSuccess: async (_, deletedId) => {
      console.log('✅ Position deletion success callback:', deletedId);
      
      // Optimistic update: immediately remove from cache
      rqClient.setQueryData(["/api/positions"], (oldData: any) => {
        if (!oldData) return oldData;
        const prev = Array.isArray(oldData) ? oldData : (Array.isArray(oldData?.data) ? oldData.data : []);
        return prev.filter((item: any) => item.id !== deletedId);
      });
      
      // Final sync with server
      await rqClient.invalidateQueries({ queryKey: ["/api/positions"] });
      
      toast({ title: t('messages.success.deleted') });
    },
    onError: (error) => {
      console.error('❌ Position deletion error:', error);
      // Revert optimistic update on error
      rqClient.invalidateQueries({ queryKey: ["/api/positions"] });
      toast({ 
        title: t('messages.error.delete'), 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Helper functions for handling operations
  const handleCreateEntry = () => {
    if (!newEntryText.trim()) return;
    
    if (activeTab === "status") {
      const data = { name: newEntryText.trim(), color: newEntryColor };
      createStatusMutation.mutate(data);
    } else if (activeTab === "priority") {
      const data = { name: newEntryText.trim(), color: newEntryColor };
      createPriorityMutation.mutate(data);
    } else {
      const data = { name: newEntryText.trim() };
      if (activeTab === "gewerk") {
        createGewerkMutation.mutate(data);
      } else if (activeTab === "typ") {
        createTypMutation.mutate(data);
      } else if (activeTab === "position") {
        createPositionMutation.mutate(data);
      }
    }
  };

  const handleEditEntry = (item: any) => {
    setEditingItem(item);
    setEditText(item.name);
    setEditColor(item.color || '#6b7280');
    setIsEditModalOpen(true);
  };

  const handleUpdateEntry = () => {
    if (!editText.trim() || !editingItem) return;
    
    if (activeTab === "status") {
      updateStatusMutation.mutate({
        id: editingItem.id,
        name: editText.trim(),
        color: editColor
      });
    } else if (activeTab === "priority") {
      updatePriorityMutation.mutate({
        id: editingItem.id,
        name: editText.trim(),
        color: editColor
      });
    } else if (activeTab === "position") {
      updatePositionMutation.mutate({
        id: editingItem.id,
        name: editText.trim()
      });
    } else if (activeTab === "gewerk") {
      updateGewerkMutation.mutate({
        id: editingItem.id,
        name: editText.trim()
      });
    } else if (activeTab === "typ") {
      updateTypMutation.mutate({
        id: editingItem.id,
        name: editText.trim()
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setEditingItem(null);
    setEditText("");
    setEditColor("#3B82F6");
  };

  const isDefaultItem = (item: any) => {
    // Gewerk and Position items are always editable/deletable, even defaults
    // Users can modify their organization's trade and position lists as needed
    if (activeTab === 'gewerk' || activeTab === 'position') {
      return false;
    }
    
    // Check if item is a default using the isDefault flag from API
    // No longer using hardcoded arrays - defaults are determined by the is_default database column
    return Boolean(item.is_default || item.isDefault);
  };

  const handleBulkDeleteEntries = async () => {
    if (selectedIds.length === 0) return;

    const confirmed = await confirm({
      message: t('confirmations.deleteItems', { count: selectedIds.length }),
      variant: 'destructive',
      confirmText: t('buttons.delete'),
    });

    if (!confirmed) return;

    selectedIds.forEach((id) => {
      if (activeTab === "gewerk") {
        deleteGewerkMutation.mutate(id);
      } else if (activeTab === "typ") {
        deleteTypMutation.mutate(id);
      } else if (activeTab === "status") {
        deleteStatusMutation.mutate(id);
      } else if (activeTab === "priority") {
        deletePriorityMutation.mutate(id);
      } else if (activeTab === "position") {
        deletePositionMutation.mutate(id);
      }
    });

    setSelectedIds([]);
  };



  // Get current data based on active tab - memoized to prevent unnecessary re-renders
  const currentData = useMemo(() => {
    if (activeTab === "gewerk") return gewerke;
    if (activeTab === "typ") return typen;
    if (activeTab === "status") return status;
    if (activeTab === "priority") return priorities;
    if (activeTab === "position") return positions;
    return [];
  }, [activeTab, gewerke, typen, status, priorities, positions]);

  // Memoize the current IDs to prevent infinite loops in useEffect
  const currentDataIds = useMemo(() => 
    new Set(currentData.map((item: any) => item.id)), 
    [currentData]
  );
  const filteredData = currentData.filter((item: any) => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (item.name || '').toLowerCase().includes(searchLower);
  });
  const sortedData = [...filteredData].sort((a, b) => {
    // Column-specific sorting takes precedence
    if (sortColumn) {
      let result = 0;
      switch (sortColumn) {
        case "number":
          // Sort by index/number
          const aIndex = currentData.indexOf(a);
          const bIndex = currentData.indexOf(b);
          result = aIndex - bIndex;
          break;
        case "name":
          const aName = a.displayName || a.name;
          const bName = b.displayName || b.name;
          result = aName.localeCompare(bName, i18n.language);
          break;
        case "project":
          const aProject = (a as any).projectName || "";
          const bProject = (b as any).projectName || "";
          result = aProject.localeCompare(bProject, i18n.language);
          break;
        default:
          result = 0;
      }
      // Apply direction (desc reverses the result)
      return sortDirection === "asc" ? result : -result;
    }
    
    // Default global sorting
    if (sortBy === "name") {
      const aName = a.displayName || a.name;
      const bName = b.displayName || b.name;
      return aName.localeCompare(bName, i18n.language);
    } else {
      // Sort by recent (createdAt)
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bDate - aDate;
    }
  });

  useEffect(() => {
    setSelectedIds([]);
    if (activeTab !== 'documents') {
      deleteSelectedDocumentsRef.current = null;
      editSelectedDocumentRef.current = null;
      setDocumentSelectionCount(0);
    }
  }, [activeTab, organizationId]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const filtered = prev.filter((id) => currentDataIds.has(id));
      // Only update if there's an actual change to prevent infinite loops
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [currentDataIds]);

  const selectableData = sortedData.filter((item) => !isDefaultItem(item));
  const allSelected = selectableData.length > 0 && selectableData.every((item) => selectedIds.includes(item.id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const isLoading = isLoadingGewerke || isLoadingTypen || isLoadingStatus || isLoadingPriorities || isLoadingPositions || isLoadingDocuments;

  if (isLoading) {
    return (
      <AppShell>
        <div className="flex-1 flex items-center justify-center">
          <LogoLoader size="lg" text={t('loading')} showText />
        </div>
      </AppShell>
    );
  }

  // Block contractors from accessing lists page (after all hooks)
  if (isContractor) {
    return (
      <AppShell>
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h1 className="text-lg font-bold text-slate-900">{t('title')}</h1>
              <OrganizationSelector />
            </div>
          </div>
        </header>
        {/* Content Area */}
        <div className="flex-1 overflow-y-auto px-3 pt-3 pb-0">
          <AccessDenied permission="manage_lists" />
        </div>
      </AppShell>
    );
  }

  // Check if user has permission to access lists
  if (!hasPermission('manage_lists')) {
    return (
      <AppShell>
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h1 className="text-lg font-bold text-slate-900">{t('title')}</h1>
              <OrganizationSelector />
            </div>
          </div>
        </header>
        {/* Content Area */}
        <div className="flex-1 overflow-y-auto px-3 pt-3 pb-0">
          <AccessDenied permission="manage_lists" />
        </div>
      </AppShell>
    );
  }


  // Get button text for current tab
  const getButtonText = () => {
    if (activeTab === "gewerk") return t('buttons.create.gewerk');
    if (activeTab === "typ") return t('buttons.create.typ');
    if (activeTab === "status") return t('buttons.create.status');
    if (activeTab === "priority") return t('buttons.create.priority');
    if (activeTab === "position") return t('buttons.create.position');
    if (activeTab === "documents") return t('documents.upload');
    
    return "";
  };
  
  // Get button onClick handler for current tab
  const getButtonOnClick = () => {
    if (activeTab === "documents") {
      return () => {
        setTriggerDocumentUpload(true);
      };
    }
    return () => {
      setIsNewEntryModalOpen(true);
    };
  };

  // Get modal title for current tab
  const getModalTitle = () => {
    if (activeTab === "gewerk") return t('modal.title.gewerk');
    if (activeTab === "typ") return t('modal.title.typ');
    if (activeTab === "status") return t('modal.title.status');
    if (activeTab === "priority") return t('modal.title.priority');
    if (activeTab === "position") return t('modal.title.position');
    
    return "";
  };

  // Helper function to get localized name for list items
  // Uses displayName from API (database-backed translations) with fallback to name
  const getLocalizedName = (item: any, _tab: string): string => {
    // The API now returns a displayName field that contains the translated name
    // based on the Accept-Language header. Fall back to name if displayName is not available.
    return item.displayName || item.name;
  };

  return (
    <AppShell>
      <PageHeaderLayout
          pageTitle={t('title')}
          searchComponent={
            <HeaderSearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('searchPlaceholder')}
            />
          }
          tabs={
            <HeaderTabNav
              items={[
                {
                  id: 'position',
                  label: t('tabs.position'),
                  active: activeTab === 'position',
                  onClick: () => handleTabChange('position'),
                },
                {
                  id: 'gewerk',
                  label: t('tabs.gewerk'),
                  active: activeTab === 'gewerk',
                  onClick: () => handleTabChange('gewerk'),
                },
                ...(hasPermission('manage_lists')
                  ? [
                      {
                        id: 'typ',
                        label: t('tabs.typ'),
                        active: activeTab === 'typ',
                        onClick: () => handleTabChange('typ'),
                      },
                      {
                        id: 'status',
                        label: t('tabs.status'),
                        active: activeTab === 'status',
                        onClick: () => handleTabChange('status'),
                      },
                      {
                        id: 'priority',
                        label: t('tabs.priority'),
                        active: activeTab === 'priority',
                        onClick: () => handleTabChange('priority'),
                      },
                    ]
                  : []),
                ...(hasPermission('manage_lists')
                  ? [
                      {
                        id: 'documents',
                        label: t('documents.title'),
                        active: activeTab === 'documents',
                        onClick: () => handleTabChange('documents'),
                      },
                    ]
                  : []),
              ]}
            />
          }
          actionButtons={
            <HeaderActionGroup className="gap-2">
              {activeTab === "documents" ? (
                <PermissionButton
                  variant="pillOutline"
                  permission="manage_lists"
                  fallbackText="You don't have permission to upload documents"
                  onClick={getButtonOnClick()}
                  size="sm"
                  className="bg-white hover:bg-white"
                >
                  + {getButtonText()}
                </PermissionButton>
              ) : (
                <PermissionButton
                  variant="pillOutline"
                  permission="manage_lists"
                  fallbackText="You don't have permission to manage lists"
                  onClick={getButtonOnClick()}
                  size="sm"
                  className="bg-white hover:bg-white"
                >
                  {getButtonText()}
                </PermissionButton>
              )}
            </HeaderActionGroup>
          }
        >
          {/* Tab Content */}
          <div className="p-4 pt-2 -mt-[8px] h-full flex flex-col min-h-0">
            {activeTab === 'documents' ? (
              <RequirePermission permission="manage_lists">
                {organizationId ? (
                  <div className="flex gap-4 max-h-full min-h-0">
                    <div className="flex-1 bg-[#DDDDDD] rounded-[24px] p-[32px] flex flex-col min-h-0 max-h-full">
                      <OrganizationDocumentsTab
                        documents={orgDocuments}
                        organizationId={organizationId}
                        triggerUpload={triggerDocumentUpload}
                        onUploadTriggered={() => setTriggerDocumentUpload(false)}
                        searchTerm={searchTerm}
                        onEditSelectedReady={handleEditSelectedReady}
                        onDeleteSelectedReady={handleDeleteSelectedReady}
                        onSelectionChange={setDocumentSelectionCount}
                      />
                    </div>
                    <RightSidebar>
                      <SidebarButtonGroup label={t('buttons.edit')} hideLabel>
                        <SidebarIconButton
                          icon={iconEditSvg}
                          alt="Edit"
                          onClick={() => {
                            if (!editSelectedDocumentRef.current) return;
                            void editSelectedDocumentRef.current();
                          }}
                          disabled={documentSelectionCount !== 1}
                          title={t('buttons.edit')}
                        />
                      </SidebarButtonGroup>
                      <SidebarButtonGroup label={t('buttons.delete')} hideLabel>
                        <SidebarIconButton
                          icon={iconDeleteSvg}
                          alt="Delete"
                          onClick={() => deleteSelectedDocumentsRef.current?.()}
                          disabled={documentSelectionCount === 0}
                          title={t('buttons.delete')}
                        />
                      </SidebarButtonGroup>
                    </RightSidebar>
                  </div>
                ) : null}
              </RequirePermission>
            ) : (
              <div className="flex gap-4 max-h-full min-h-0">
                <div className="flex-1 bg-[#DDDDDD] rounded-[24px] p-[32px] flex flex-col min-h-0 max-h-full">
                  <div className="bg-white border border-slate-200 overflow-hidden rounded-none shadow-none flex flex-col min-h-0 max-h-full">
                    <div className="overflow-auto min-h-0 pb-4">
                          <table className="min-w-full listen-table">
                            {/* Table Header for sortable columns */}
                            <thead className="sticky top-0 z-10">
                              <tr className="table-header-row">
                                <th className="table-header-cell w-10">
                                  <Checkbox
                                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                                    onCheckedChange={() => {
                                      if (allSelected) {
                                        setSelectedIds([]);
                                        return;
                                      }
                                      setSelectedIds(selectableData.map((item) => item.id));
                                    }}
                                    aria-label={t('table.headers.selectAll')}
                                  />
                                </th>
                                <th className="table-header-cell w-12">
                                  <button 
                                    onClick={() => {
                                      const newColumn = "number";
                                      const newDirection = sortColumn === newColumn && sortDirection === "asc" ? "desc" : "asc";
                                      setSortColumn(newColumn);
                                      setSortDirection(newDirection);
                                    }}
                                    className="flex items-center gap-1 hover:text-slate-900"
                                  >
                                    {t('table.headers.number')}
                                    {sortColumn === "number" && <span className="text-[10px]">{sortDirection === "asc" ? "↓" : "↑"}</span>}
                                  </button>
                                </th>
                                <th className="table-header-cell flex-1">
                                  <button 
                                    onClick={() => {
                                      const newColumn = "name";
                                      const newDirection = sortColumn === newColumn && sortDirection === "asc" ? "desc" : "asc";
                                      setSortColumn(newColumn);
                                      setSortDirection(newDirection);
                                    }}
                                    className="flex items-center gap-1 hover:text-slate-900"
                                  >
                                    {t('table.headers.name')}
                                    {sortColumn === "name" && <span className="text-[10px]">{sortDirection === "asc" ? "↓" : "↑"}</span>}
                                  </button>
                                </th>
                                {(activeTab === "status" || activeTab === "priority") && (
                                  <th className="table-header-cell w-16">
                                    <span>{t('table.headers.color') || 'Color'}</span>
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {sortedData.map((item, index) => (
                                <tr key={item.id} className="table-body-row">
                                  <td className="table-body-cell whitespace-nowrap">
                                    <Checkbox
                                      checked={selectedIds.includes(item.id)}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setSelectedIds((prev) => [...prev, item.id]);
                                        } else {
                                          setSelectedIds((prev) => prev.filter((id) => id !== item.id));
                                        }
                                      }}
                                      disabled={isDefaultItem(item)}
                                      aria-label={t('table.headers.selectRow')}
                                    />
                                  </td>
                                  <td className="table-body-cell whitespace-nowrap">
                                    {String(index + 1).padStart(2, '0')}
                                  </td>
                                  <td className="table-body-cell">
                                    {getLocalizedName(item, activeTab)}
                                  </td>
                                  {(activeTab === "status" || activeTab === "priority") && (
                                    <td className="table-body-cell">
                                      <div 
                                        className="w-4 h-4 rounded-full border border-slate-300"
                                        style={{ backgroundColor: (item as any).color || '#6b7280' }}
                                      />
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                
                          {sortedData.length === 0 && (
                            <div className="py-4 text-center text-xs text-slate-500">
                              {t('table.empty')}
                            </div>
                          )}
                    </div>
                  </div>
                </div>

                {/* Right Sidebar Menu */}
                <RightSidebar>
                  <SidebarButtonGroup label={t('buttons.edit')} hideLabel>
                    <SidebarIconButton
                      icon={iconEditSvg}
                      alt="Edit"
                      onClick={() => {
                        if (selectedIds.length === 1) {
                          const item = sortedData.find((d) => d.id === selectedIds[0]);
                          if (item && !isDefaultItem(item)) {
                            handleEditEntry(item);
                          }
                        }
                      }}
                      disabled={selectedIds.length !== 1 || (selectedIds.length === 1 && isDefaultItem(sortedData.find((d) => d.id === selectedIds[0])))}
                      title={t('buttons.edit')}
                    />
                  </SidebarButtonGroup>
                  <SidebarButtonGroup label={t('buttons.delete')} hideLabel>
                    <SidebarIconButton
                      icon={iconDeleteSvg}
                      alt="Delete"
                      onClick={handleBulkDeleteEntries}
                      disabled={selectedIds.length === 0}
                      title={t('buttons.delete')}
                    />
                  </SidebarButtonGroup>
                </RightSidebar>
              </div>
            )}
          </div>
      </PageHeaderLayout>

      {/* Modal for creating new entries */}
      <Dialog open={isNewEntryModalOpen} onOpenChange={setIsNewEntryModalOpen}>
        <DialogContent 
          className="sm:max-w-md bg-[#DDDDDD] rounded-[24px] [&>button]:hidden"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Return') {
              const target = e.target as HTMLElement;
              // Nur speichern wenn nicht in einem Input (da die Inputs bereits eigene Enter-Handler haben)
              if (!target.matches('input') && !target.matches('[role="combobox"]')) {
                e.preventDefault();
                console.log('💾 Enter-Taste: Erstelle Eintrag...', { activeTab, newEntryText });
                handleCreateEntry();
              }
            }
          }}
          tabIndex={0}
        >
          <div className="flex items-start justify-between gap-4">
            <DialogHeader className="text-left">
              <DialogTitle className="text-sm font-semibold">{getModalTitle()}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                {t('modal.descriptionText')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2">
              <IconButtonCancel
                onClick={() => {
                  setIsNewEntryModalOpen(false);
                  setNewEntryText("");
                  setNewEntryColor("#3B82F6");
                }}
                size={30}
                title={t('buttons.cancel')}
              />
              <IconButtonSave
                onClick={handleCreateEntry}
                disabled={!newEntryText.trim()}
                size={30}
                title={t('buttons.save')}
              />
            </div>
          </div>
          <div className="space-y-6">
            <CornerLabelField label={t('modal.fields.name')}>
              <PermissionInput
                id="entry-name"
                value={newEntryText}
                onChange={(e) => setNewEntryText(e.target.value)}
                placeholder={t('modal.fields.namePlaceholder')}
                className="w-full"
                permission="manage_lists"
                fallbackText="You don't have permission to manage lists"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateEntry();
                  }
                }}
              />
            </CornerLabelField>
            {(activeTab === "status" || activeTab === "priority") && (
              <CornerLabelField label={t('modal.fields.color')} className="mt-4 pt-5">
                <div className="mt-1 flex flex-col gap-3">
                  <ColorPicker
                    value={newEntryColor}
                    onChange={setNewEntryColor}
                  />
                </div>
              </CornerLabelField>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal for all tabs */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent 
          className="sm:max-w-md bg-[#DDDDDD] rounded-[24px] [&>button]:hidden"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Return') {
              const target = e.target as HTMLElement;
              if (!target.matches('input') && !target.matches('[role="combobox"]')) {
                e.preventDefault();
                handleUpdateEntry();
              }
            }
          }}
          tabIndex={0}
        >
          <div className="flex items-start justify-between gap-4">
            <DialogHeader className="text-left">
              <DialogTitle className="text-sm font-semibold">
                {activeTab === "priority" ? (t('modal.title.editPriority') || 'Edit Priority') : 
                 activeTab === "status" ? (t('modal.title.editStatus') || 'Edit Status') :
                 activeTab === "position" ? (t('modal.title.editPosition') || 'Edit Position') :
                 activeTab === "gewerk" ? (t('modal.title.editGewerk') || 'Edit Trade') :
                 activeTab === "typ" ? (t('modal.title.editTyp') || 'Edit Type') : 
                 'Edit Entry'}
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                {(activeTab === "status" || activeTab === "priority") 
                  ? (t('modal.description.edit') || `Update the ${activeTab === "priority" ? "priority" : "status"} name and color.`)
                  : (t('modal.description.editName') || `Update the ${activeTab} name.`)}
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2">
              <IconButtonCancel
                onClick={handleCancelEdit}
                size={30}
                title={t('buttons.cancel')}
              />
              <IconButtonSave
                onClick={handleUpdateEntry}
                disabled={!editText.trim() || (editingItem && (editingItem.is_default || editingItem.isDefault))}
                size={30}
                title={t('buttons.save')}
              />
            </div>
          </div>
          <div className="space-y-6">
            <CornerLabelField label={t('modal.fields.name')}>
              <Input
                id="edit-name"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder={t('modal.fields.namePlaceholder')}
                className="w-full"
                disabled={editingItem && (editingItem.is_default || editingItem.isDefault)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleUpdateEntry();
                  }
                }}
              />
            </CornerLabelField>
            {(activeTab === "status" || activeTab === "priority") && (
              <CornerLabelField label={t('modal.fields.color')} className="mt-4 pt-5">
                <div className="mt-1 flex flex-col gap-3">
                  <ColorPicker
                    value={editColor}
                    onChange={setEditColor}
                    disabled={false}
                  />
                </div>
                {/* Note: Color editing is now allowed for all statuses, including defaults */}
              </CornerLabelField>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
